import axiosLib from 'axios';
import Cookies from 'js-cookie';

// Crear una instancia de Axios con credenciales habilitadas por defecto
const axios = axiosLib.create({ withCredentials: true });

// Utilidades para leer/escribir tokens en cookies con las mismas opciones que auth.js
function setTokenCookie(name, value) {
	const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
	Cookies.set(name, value, {
		secure: !isLocal,
		sameSite: isLocal ? 'lax' : 'none',
		domain: isLocal ? undefined : '.inmob.com.ar',
		path: '/',
	});
}

function removeTokenCookie(name) {
	try { Cookies.remove(name, { domain: '.inmob.com.ar', path: '/' }); } catch {}
	try { Cookies.remove(name); } catch {}
}

// Request interceptor: adjuntar Authorization si existe token en cookies
axios.interceptors.request.use((config) => {
	const token = Cookies.get('token');
	config.headers = config.headers || {};
	if (token && !config.headers.Authorization) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	if (window.location.host && !config.headers['X-Inmob-Domain']) {
		config.headers['X-Inmob-Domain'] = window.location.host;
	}
	return config;
});

// Control de actualización de token para evitar múltiples llamadas simultáneas
let isRefreshing = false;
let refreshPromise = null;
let pendingQueue = [];

function onRefreshed(newToken) {
	pendingQueue.forEach(({ resolve }) => resolve(newToken));
	pendingQueue = [];
}

function onRefreshFailed(error) {
	pendingQueue.forEach(({ reject }) => reject(error));
	pendingQueue = [];
}

// Response interceptor: intentar refrescar token en 401 token_not_valid
axios.interceptors.response.use(
	(response) => response,
	async (error) => {
		const { response, config: originalRequest } = error || {};
		if (!response) return Promise.reject(error);

			const status = response.status;
			const code = response.data?.code;
			const detail = (response.data?.detail || '').toString().toLowerCase();

			const isAuthError =
				status === 401 && (
					code === 'token_not_valid' ||
					detail.includes('token') ||
					detail.includes('credencial') ||
					detail.includes('caduc') || // caducó/caduco
					detail.includes('expir') // expiró/expirado
				);
		// Solo intentar refresh si no es ya un reintento del refresh endpoint y tenemos refreshToken
		if (isAuthError && !originalRequest._retry) {
			const refreshToken = Cookies.get('refreshToken');
			if (!refreshToken) {
				removeTokenCookie('token');
				removeTokenCookie('refreshToken');
				return Promise.reject(error);
			}

			// Marcar para evitar bucles
			originalRequest._retry = true;

			// Si ya hay una actualización en curso, encolar el request hasta que termine
			if (isRefreshing) {
				try {
					const newTok = await new Promise((resolve, reject) => {
						pendingQueue.push({ resolve, reject });
					});
					originalRequest.headers = originalRequest.headers || {};
					originalRequest.headers.Authorization = `Bearer ${newTok}`;
					return axios(originalRequest);
				} catch (e) {
					return Promise.reject(e);
				}
			}

			// Iniciar refresh
			isRefreshing = true;
			const refreshUrl = `${process.env.VUE_APP_API_BASE_URL}/api/accounts/token/refresh/`;
			try {
				refreshPromise = axiosLib.post(refreshUrl, { refresh: refreshToken }, { withCredentials: true });
				const { data } = await refreshPromise;
				const newAccess = data?.access;
				if (!newAccess) throw new Error('Refresh sin access token');

				// Guardar nuevo access en cookie y continuar
				setTokenCookie('token', newAccess);
				onRefreshed(newAccess);

				// Reintentar request original con nuevo token
				originalRequest.headers = originalRequest.headers || {};
				originalRequest.headers.Authorization = `Bearer ${newAccess}`;
				return axios(originalRequest);
			} catch (refreshErr) {
				onRefreshFailed(refreshErr);
				removeTokenCookie('token');
				removeTokenCookie('refreshToken');
				return Promise.reject(refreshErr);
			} finally {
				isRefreshing = false;
				refreshPromise = null;
			}
		}

		return Promise.reject(error);
	}
);

export default axios;
